import { Injectable } from '@angular/core';
import { DatetimeService } from './datetime.service';
import { AuditLogResponse } from '../../pages/audit/models/audit.model';

/** Fila de diff lista para mostrar al usuario final. */
export interface AuditDiffRow {
  field: string;
  label: string;
  oldVal: string;
  newVal: string;
}

/**
 * Única fuente de presentación de auditoría para el usuario final:
 * traducción de entidades/operaciones/subtipos/severidades, títulos de timeline
 * y construcción de filas de diff (prefiere `changesDisplay` del backend y cae
 * al diff técnico oldValues/newValues cuando no existe).
 *
 * Usado por las pantallas de `pages/audit/` y por los tabs "Timeline" de los
 * detail-modals (contactos, deals, leads, empresas, tareas, productos, formularios).
 */
@Injectable({ providedIn: 'root' })
export class AuditDisplayService {

  constructor(private datetime: DatetimeService) {}

  // ─────────────────────────── Entidades ───────────────────────────

  private readonly ENTITIES: Record<string, { label: string; gender: 'M' | 'F' }> = {
    contact: { label: 'Contacto', gender: 'M' },
    company: { label: 'Empresa', gender: 'F' },
    // Tabla `bussines` (multi-tenant): en la UI es "Empresa" del módulo Businesses.
    bussines: { label: 'Empresa', gender: 'F' },
    business: { label: 'Empresa', gender: 'F' },
    lead: { label: 'Lead', gender: 'M' },
    deal: { label: 'Negocio', gender: 'M' },
    quote: { label: 'Cotización', gender: 'F' },
    quoteitem: { label: 'Ítem de cotización', gender: 'M' },
    quote_item: { label: 'Ítem de cotización', gender: 'M' },
    quote_template: { label: 'Plantilla de cotización', gender: 'F' },
    meeting: { label: 'Reunión', gender: 'F' },
    meetingattendee: { label: 'Asistente de reunión', gender: 'M' },
    meeting_attendee: { label: 'Asistente de reunión', gender: 'M' },
    task: { label: 'Tarea', gender: 'F' },
    task_comment: { label: 'Comentario de tarea', gender: 'M' },
    task_follower: { label: 'Seguidor de tarea', gender: 'M' },
    task_contact: { label: 'Contacto de tarea', gender: 'M' },
    task_dependency: { label: 'Dependencia de tarea', gender: 'F' },
    task_status_history: { label: 'Estado de tarea', gender: 'M' },
    ticket: { label: 'Ticket', gender: 'M' },
    product: { label: 'Producto', gender: 'M' },
    price_list: { label: 'Lista de precios', gender: 'F' },
    product_price_list: { label: 'Lista de precios', gender: 'F' },
    product_bundle: { label: 'Paquete de productos', gender: 'M' },
    // Sub-entidades que aparecen como eventos dentro del timeline del paquete y de
    // la lista de precios (inc 35). Sin estas entradas el chip mostraba el nombre
    // técnico prettificado ("Product bundle item").
    product_bundle_item: { label: 'Producto del paquete', gender: 'M' },
    product_price_list_item: { label: 'Producto de la lista', gender: 'M' },
    discount_rule: { label: 'Regla de descuento', gender: 'F' },
    product_discount_rule: { label: 'Regla de descuento', gender: 'F' },
    product_category: { label: 'Categoría de producto', gender: 'F' },
    segment: { label: 'Segmento', gender: 'M' },
    campaign: { label: 'Campaña', gender: 'F' },
    template: { label: 'Plantilla', gender: 'F' },
    invitation: { label: 'Invitación', gender: 'F' },
    invitation_template: { label: 'Plantilla de invitación', gender: 'F' },
    user: { label: 'Usuario', gender: 'M' },
    activity: { label: 'Actividad', gender: 'F' },
    mkt_form: { label: 'Formulario', gender: 'M' },
    mkt_form_field: { label: 'Campo de formulario', gender: 'M' },
    mkt_form_field_option: { label: 'Opción de campo', gender: 'F' },
    mkt_form_setting: { label: 'Configuración de formulario', gender: 'F' },
    mkt_form_target_url: { label: 'URL objetivo', gender: 'F' },
    mkt_form_field_crm_map: { label: 'Mapeo CRM', gender: 'M' }
  };

  /** contact → Contacto, mkt_form → Formulario; fallback: prettify del nombre crudo. */
  translateEntity(entity?: string | null): string {
    if (!entity) return '—';
    return this.ENTITIES[entity.toLowerCase()]?.label ?? this.prettify(entity);
  }

  // ─────────────────────────── Operaciones ───────────────────────────

  /** CREATE/INSERT → Creación (se muestran igual), UPDATE → Actualización, DELETE → Eliminación. */
  translateOperation(op?: string | null): string {
    switch (op?.toUpperCase()) {
      case 'CREATE':
      case 'INSERT': return 'Creación';
      case 'UPDATE': return 'Actualización';
      case 'DELETE': return 'Eliminación';
      case 'READ': return 'Lectura';
      default: return op ? this.prettify(op) : '—';
    }
  }

  private readonly SUBTYPES: Record<string, string> = {
    CHANGE_CYCLE: 'Cambio de ciclo',
    CHANGE_STATE: 'Cambio de estado',
    STATUS_CHANGE: 'Cambio de estado',
    FIELD_CHANGE: 'Cambio de campo',
    ASSIGNMENT_CHANGE: 'Cambio de asignación',
    DISQUALIFY: 'Descalificación',
    REQUALIFY: 'Recalificación',
    DUPLICATE: 'Duplicado',
    NEW_VERSION: 'Nueva versión',
    BULK_UPDATE: 'Actualización masiva',
    BULK_DELETE: 'Eliminación masiva',
    NOTE_ADDED: 'Nota agregada',
    EMAIL_SENT: 'Correo enviado',
    EMAIL_RECEIVED: 'Correo recibido',
    CALL_MADE: 'Llamada realizada',
    CALL_RECEIVED: 'Llamada recibida',
    MEETING_SCHEDULED: 'Reunión programada',
    MEETING_COMPLETED: 'Reunión completada',
    QUOTE_SENT: 'Cotización enviada',
    QUOTE_ACCEPTED: 'Cotización aceptada',
    QUOTE_REJECTED: 'Cotización rechazada',
    DEAL_WON: 'Negocio ganado',
    DEAL_LOST: 'Negocio perdido',
    CONTACT_ADDED: 'Contacto agregado',
    CONTACT_REMOVED: 'Contacto eliminado',
    COMPANY_ADDED: 'Empresa agregada',
    COMPANY_REMOVED: 'Empresa eliminada',
    TASK_COMPLETED: 'Tarea completada',
    TASK_CREATED: 'Tarea creada',
    REMINDER_SET: 'Recordatorio establecido',
    RETRY_SCHEDULED: 'Reintento programado',
    // Contactos: la UI dice "Inactivar"/"Reactivar"; sin estas entradas el fallback
    // cliente mostraba el subtipo crudo prettificado ("Archive" / "Restore").
    ARCHIVE: 'Inactivado',
    CONTACT_ARCHIVE: 'Inactivado',
    RESTORE: 'Reactivado',
    // Empresas / Businesses (INC 18)
    BUSINESS_ACTIVE_STATE: 'Cambio de estado',
    BUSINESS_DELETE: 'Eliminación',
    // Formularios de marketing (inc28)
    FIELD_ADD: 'Campo agregado',
    FIELD_UPDATE: 'Campo actualizado',
    FIELD_REMOVE: 'Campo eliminado',
    CRM_MAP_ADD: 'Mapeo CRM agregado',
    CRM_MAP_UPDATE: 'Mapeo CRM actualizado',
    CRM_MAP_REMOVE: 'Mapeo CRM eliminado',
    SETTING_INIT: 'Configuración inicializada',
    SETTING_UPDATE: 'Configuración actualizada',
    TARGET_URL_ADD: 'URL objetivo agregada',
    TARGET_URL_REMOVE: 'URL objetivo eliminada',
    OPTION_ADD: 'Opción agregada',
    OPTION_UPDATE: 'Opción actualizada',
    OPTION_REMOVE: 'Opción eliminada'
  };

  /** CHANGE_CYCLE → "Cambio de ciclo"; fallback: prettify del subtipo crudo. */
  translateSubtype(subtype?: string | null): string {
    if (!subtype) return '';
    return this.SUBTYPES[subtype.toUpperCase()] ?? this.prettify(subtype);
  }

  // ─────────── Etiquetas enriquecidas (endpoints con *Label del backend) ───────────
  // Para GET /api/audit/logs y .../{entity}/{id}/timeline el backend ya localiza
  // el nombre/operación/subtipo. Estos helpers PREFIEREN esos campos y solo caen a
  // la traducción cliente para filas antiguas o DTOs no enriquecidos (fallback seguro).

  /**
   * Nombre legible de la entidad afectada: usa `entityDisplayName` del backend y
   * cae al id crudo cuando la entidad no tiene nombre resoluble.
   */
  entityDisplay(log: AuditLogResponse): string {
    return log.entityDisplayName ?? log.idEntity ?? '—';
  }

  /** Etiqueta del tipo de operación: prefiere `operationTypeLabel`; fallback cliente. */
  operationLabel(log: AuditLogResponse): string {
    return log.operationTypeLabel ?? this.translateOperation(log.operationType);
  }

  /** Etiqueta del subtipo: prefiere `operationSubtypeLabel`; fallback cliente. '' si no hay subtipo. */
  subtypeLabel(log: AuditLogResponse): string {
    return log.operationSubtypeLabel ?? this.translateSubtype(log.operationSubtype);
  }

  /** INFO → Info, WARN → Advertencia, ERROR → Error. */
  translateSeverity(severity?: string | null): string {
    switch (severity?.toUpperCase()) {
      case 'INFO': return 'Info';
      case 'WARN':
      case 'WARNING': return 'Advertencia';
      case 'ERROR': return 'Error';
      default: return severity ? this.prettify(severity) : '—';
    }
  }

  private readonly MODULES: Record<string, string> = {
    crmcore: 'CRM',
    auth: 'Autenticación',
    communication: 'Comunicación',
    meeting: 'Reuniones',
    product: 'Productos',
    quote: 'Cotizaciones',
    storage: 'Almacenamiento',
    invitation: 'Invitaciones',
    oauth: 'OAuth',
    logs: 'Auditoría',
    task: 'Tareas',
    ticket: 'Tickets',
    marketing: 'Marketing',
    segment: 'Segmentos'
  };

  /** crmcore → CRM, meeting → Reuniones; fallback: prettify. */
  translateModule(module?: string | null): string {
    if (!module) return '—';
    return this.MODULES[module.toLowerCase()] ?? this.prettify(module);
  }

  // ─────────────────────────── Título de timeline ───────────────────────────

  /** "Contacto actualizado", "Empresa creada", "Reunión eliminada". */
  getTimelineTitle(log: AuditLogResponse): string {
    const meta = this.ENTITIES[log.entityName?.toLowerCase() ?? ''];
    const label = meta?.label ?? this.prettify(log.entityName ?? '');
    const f = meta?.gender === 'F';
    switch (log.operationType?.toUpperCase()) {
      case 'CREATE':
      case 'INSERT': return `${label} ${f ? 'creada' : 'creado'}`;
      case 'UPDATE': return `${label} ${f ? 'actualizada' : 'actualizado'}`;
      case 'DELETE': return `${label} ${f ? 'eliminada' : 'eliminado'}`;
      default: return label;
    }
  }

  /**
   * Título legible de una entrada del timeline (inc28). Prioridad:
   *  1. `description` narrativa del backend (ya en español),
   *  2. etiqueta del subtipo cuando existe (ej. "Campo agregado", "Configuración
   *     inicializada") — evita mostrar "Creación" repetida en sub-eventos,
   *  3. título por entidad/operación ("Formulario creado").
   */
  getTimelineHeadline(log: AuditLogResponse): string {
    // inc31/28: cuando el backend cae a su plantilla genérica "…el registro de
    // {tabla} {id}" (nombre técnico de tabla + id crudo, típico en sub-entidades
    // como mkt_form_field_crm_map), NO la mostramos: preferimos la etiqueta de
    // subtipo legible ("Mapeo CRM agregado"). Las descripciones narrativas reales
    // no contienen el nombre crudo de la tabla, así que otros módulos no se ven afectados.
    if (log.description && !this.isGenericRecordDescription(log)) {
      return this.translateDescription(log.description);
    }
    const subtype = this.subtypeLabel(log);
    if (subtype) return subtype;
    return this.getTimelineTitle(log);
  }

  /**
   * ¿La `description` del backend es su plantilla genérica técnica
   * "Se {creó|actualizó|eliminó} el registro de {entityName} {id}"? Se detecta por
   * la aparición del nombre CRUDO de la tabla (`entityName`, en snake_case) tras
   * "el registro de" — señal inequívoca de que NO hubo enriquecimiento legible.
   */
  private isGenericRecordDescription(log: AuditLogResponse): boolean {
    const desc = log.description?.toLowerCase() ?? '';
    const entity = (log.entityName ?? '').toLowerCase();
    return !!entity && desc.includes(`el registro de ${entity}`);
  }

  /**
   * ¿El titular proviene de una descripción narrativa (no genérica)? Sirve para no
   * repetir el chip de subtipo cuando el titular ya ES la etiqueta de subtipo.
   */
  usesDescriptionHeadline(log: AuditLogResponse): boolean {
    return !!log.description && !this.isGenericRecordDescription(log);
  }

  // ─────────────────────────── Campos ───────────────────────────

  private readonly FIELD_LABELS: Record<string, string> = {
    id: 'ID',
    name: 'Nombre',
    fullname: 'Nombre',
    lastname: 'Apellido',
    email: 'Correo electrónico',
    mail: 'Correo electrónico',
    phone: 'Teléfono',
    status: 'Estado',
    industry: 'Industria',
    source: 'Fuente',
    priority: 'Prioridad',
    value: 'Valor',
    amount: 'Monto',
    closeDate: 'Fecha de cierre',
    actualCloseDate: 'Fecha de cierre',
    expectedCloseDate: 'Fecha esperada de cierre',
    description: 'Descripción',
    title: 'Título',
    tags: 'Etiquetas',
    budget: 'Presupuesto',
    notes: 'Notas',
    jobTitle: 'Puesto',
    department: 'Departamento',
    address: 'Dirección',
    city: 'Ciudad',
    state: 'Estado/Provincia',
    country: 'País',
    website: 'Sitio web',
    annualRevenue: 'Ingresos anuales',
    employees: 'Empleados',
    employeeCount: 'Cantidad de empleados',
    stage: 'Etapa',
    probability: 'Probabilidad',
    price: 'Precio',
    cost: 'Costo',
    quantity: 'Cantidad',
    discount: 'Descuento',
    tax: 'Impuesto',
    validUntil: 'Válido hasta',
    commercialName: 'Nombre comercial',
    legalName: 'Razón social',
    taxId: 'RFC/NIT',
    isActive: 'Activo',
    isDeleted: 'Eliminado',
    isDisqualified: 'Descalificado',
    disqualificationReason: 'Razón de descalificación',
    disqualificationNotes: 'Notas de descalificación',
    isRetryScheduled: 'Reintento programado',
    retryDate: 'Fecha de reintento',
    lossReason: 'Razón de pérdida',
    wonReason: 'Razón de ganancia',
    createdAt: 'Fecha de creación',
    updatedAt: 'Última actualización',
    datetimeCreated: 'Fecha de creación',
    datetimeUpdated: 'Última actualización',
    datetimeDue: 'Vencimiento',
    userRegisterId: 'Registrado por',
    userPropietyId: 'Responsable',
    ownerId: 'Responsable',
    accountManagerId: 'Gestor de cuenta',
    idCompany: 'Empresa',
    idContact: 'Contacto',
    idOrigin: 'Origen',
    idContactCycle: 'Ciclo',
    idContactState: 'Estado',
    idLeadCycle: 'Ciclo',
    // Inc 40: en Leads, lead_state es el "Tipo de Lead" (no un estado).
    idLeadState: 'Tipo',
    idDealCycle: 'Ciclo',
    idDealState: 'Estado',
    idParentLead: 'Lead padre',
    idDisqualificationReason: 'Razón de descalificación',
    idCategory: 'Categoría',
    idTaskStatus: 'Estado',
    idTaskPriority: 'Prioridad',
    idTaskType: 'Tipo',
    idTaskCategory: 'Categoría',
    intIdUserOwner: 'Responsable',
    businessId: 'Negocio (tenant)',
    sku: 'SKU',
    currencyId: 'Moneda',
    currencyCode: 'Moneda',
    stockQuantity: 'Stock',
    unitOfMeasure: 'Unidad de medida',
    imageUrl: 'Imagen',
    // Empresas / Businesses (INC 18) — fallback cuando no viene `changesDisplay`
    identificationType: 'Tipo de identificación',
    identificationNumber: 'Número de identificación',
    defaultTimezone: 'Zona horaria',
    defaultCurrencyId: 'Moneda',
    logoUrl: 'Logo',
    color: 'Color',
    deletedAt: 'Fecha de eliminación',
    deleted_at: 'Fecha de eliminación',
    // Formularios de marketing (inc31) — camelCase y snake_case
    isTemplate: 'Es plantilla',
    is_template: 'Es plantilla',
    isProtected: 'Protegido',
    is_protected: 'Protegido',
    createdBy: 'Creado por',
    created_by: 'Creado por',
    updatedBy: 'Actualizado por',
    updated_by: 'Actualizado por',
    formType: 'Tipo de formulario',
    form_type: 'Tipo de formulario',
    internalNotes: 'Notas internas',
    internal_notes: 'Notas internas',
    slug: 'Slug',
    isMultiStep: 'Multi-paso',
    is_multi_step: 'Multi-paso',
    totalSteps: 'Total de pasos',
    total_steps: 'Total de pasos',
    submitButtonLabel: 'Texto del botón',
    submit_button_label: 'Texto del botón',
    hideProviderBranding: 'Ocultar marca',
    hide_provider_branding: 'Ocultar marca',
    confirmEmailEnabled: 'Email de confirmación',
    confirm_email_enabled: 'Email de confirmación',
    notifyTeamEnabled: 'Notificar al equipo',
    notify_team_enabled: 'Notificar al equipo',
    fieldKey: 'Clave del campo',
    field_key: 'Clave del campo',
    fieldType: 'Tipo de campo',
    field_type: 'Tipo de campo',
    isRequired: 'Obligatorio',
    is_required: 'Obligatorio',
    stepNumber: 'Paso',
    step_number: 'Paso',
    urlPattern: 'Patrón de URL',
    url_pattern: 'Patrón de URL',
    matchType: 'Tipo de coincidencia',
    match_type: 'Tipo de coincidencia',
    // Productos / paquetes / listas de precios / reglas de descuento (inc 28 y 35).
    // Fallback para filas históricas sin `changesDisplay`, que antes se mostraban
    // con el nombre técnico en inglés ("Is taxable", "Tax percent"...).
    type: 'Tipo',
    subtype: 'Subtipo',
    ref: 'Referencia',
    prefix: 'Prefijo de moneda',
    skuPrefix: 'Prefijo de SKU',
    taxPercent: 'Impuesto %',
    isTaxable: 'Gravable',
    maxDiscountPercent: 'Descuento máximo %',
    minPrice: 'Precio mínimo',
    minStockLevel: 'Stock mínimo',
    isTrackable: 'Rastreable',
    isRecurring: 'Recurrente',
    billingCycle: 'Ciclo de facturación',
    billingCondition: 'Condición de facturación',
    fixedPaymentCount: 'Número de pagos',
    durationHours: 'Horas de servicio',
    margin: 'Margen',
    usesTierPricing: 'Usa precios escalonados',
    hasVariants: 'Tiene variantes',
    promoValidFrom: 'Promoción — inicio de vigencia',
    promoValidUntil: 'Promoción — fin de vigencia',
    promoPrice: 'Precio promocional',
    promoEnabled: 'Promoción activa',
    // Paquetes
    bundlePrice: 'Precio del paquete',
    calculatedPrice: 'Precio calculado',
    discountPercent: 'Descuento %',
    discountAmount: 'Descuento (monto)',
    priceStrategy: 'Estrategia de precio',
    displayMode: 'Modo de visualización',
    bundleRole: 'Rol del paquete',
    isOptional: 'Opcional',
    // Listas de precios
    isDefault: 'Predeterminada',
    priceType: 'Tipo de precio',
    resolvedPrice: 'Precio resuelto',
    minQuantity: 'Cantidad mínima',
    totalItems: 'Total de productos',
    // Reglas de descuento
    ruleType: 'Tipo de regla',
    discountType: 'Tipo de descuento',
    discountValue: 'Valor del descuento',
    maxQuantity: 'Cantidad máxima',
    couponCode: 'Código de cupón',
    maxUses: 'Máximo de usos',
    currentUses: 'Usos actuales',
    minSalePrice: 'Precio mínimo de venta',
    validFrom: 'Válido desde',
    productId: 'Producto',
    contactId: 'Contacto',
    // Sub-entidades de Productos que aún caían a prettify() en filas sin `changesDisplay`.
    idProduct: 'Producto',
    idBundle: 'Paquete',
    priceListId: 'Lista de precios',
    pricingMethod: 'Método de cálculo',
    tierLevel: 'Nivel',
    minUnits: 'Unidades mínimas',
    maxUnits: 'Unidades máximas',
    fixedPrice: 'Precio fijo',
    priceOverride: 'Precio propio',
    priceSurcharge: 'Recargo',
    displayOrder: 'Orden',
    attachmentType: 'Tipo de adjunto',
    appliesTo: 'Aplica a',
    usageLimit: 'Límite de uso',
    rateToBase: 'Tipo de cambio'
  };

  /**
   * Valores de enumeración traducidos, por `entidad.campo`. Espejo del diccionario del backend
   * (`AuditDisplayEnricher.VALUE_LABELS_BY_ENTITY_FIELD`), necesario porque `changes_display` se
   * PERSISTE al escribir el log: las filas anteriores al fix del backend siguen guardando el valor
   * crudo ("sum_of_components", "fixed", "service") y solo se pueden traducir al pintarlas.
   *
   * Las etiquetas son las mismas de los `<option>` de los formularios del módulo Productos.
   */
  private readonly ENUM_VALUE_LABELS: Record<string, Record<string, string>> = {
    'product.type': {
      inventory: 'Con inventario',
      no_inventory: 'Sin inventario',
      non_inventory: 'Sin inventario',
      service: 'Servicio'
    },
    'product.subtype': {
      resources: 'Recursos',
      consulting: 'Consultoría'
    },
    'product.status': {
      draft: 'Borrador',
      active: 'Activo',
      discontinued: 'Descontinuado',
      archived: 'Archivado'
    },
    'product.billingCycle': {
      daily: 'Diario',
      weekly: 'Semanal',
      monthly: 'Mensual',
      quarterly: 'Trimestral',
      biannual: 'Semestral',
      yearly: 'Anual',
      annual: 'Anual',
      'one-time': 'Una sola vez',
      one_time: 'Una sola vez'
    },
    'product.billingCondition': {
      auto_renew: 'Renovación automática',
      fixed_payments: 'Pagos fijos',
      manual: 'Manual',
      on_completion: 'Al completar',
      prepaid: 'Prepago',
      postpaid: 'Pospago'
    },
    'product_bundle.priceStrategy': {
      fixed: 'Precio fijo',
      sum_of_components: 'Suma de componentes',
      percentage_discount: 'Descuento porcentual',
      manual: 'Manual'
    },
    'product_bundle.displayMode': {
      single_line: 'Línea única',
      breakdown: 'Desglose',
      grouped: 'Agrupado',
      combined: 'Combinado',
      single: 'Único'
    },
    'product_bundle.bundleRole': {
      standard: 'Estándar',
      order_bump: 'Order Bump',
      upsell: 'Upsell'
    },
    'product_bundle_item.bundleRole': {
      standard: 'Estándar',
      order_bump: 'Order Bump',
      upsell: 'Upsell'
    },
    'product_price_list_item.priceType': {
      fixed: 'Precio fijo',
      discount_percent: 'Descuento %',
      discount_amount: 'Descuento monto'
    },
    'product_price_currency.priceType': {
      fixed: 'Precio fijo',
      calculated: 'Calculado'
    },
    'product_tier_price.pricingMethod': {
      volume: 'Volumen',
      step: 'Escalonado (step)',
      graduated: 'Graduado'
    },
    'product_discount_rule.ruleType': {
      volume: 'Volumen',
      contractual: 'Contractual',
      coupon: 'Cupón',
      min_price: 'Precio mínimo'
    },
    'product_discount_rule.discountType': {
      percent: 'Porcentaje',
      fixed_amount: 'Monto fijo'
    },
    'product_category.type': {
      product: 'Producto',
      service: 'Servicio',
      both: 'Ambos'
    }
  };

  /** Etiqueta en español del campo; fallback: prettify de camelCase/snake_case. */
  prettifyField(field: string): string {
    return this.FIELD_LABELS[field] ?? this.prettify(field);
  }

  /** "idContactCycle" → "Id contact cycle"; "on_hold" → "On hold". */
  private prettify(value: string): string {
    const spaced = value
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z\d])([A-Z])/g, '$1 $2')
      .trim()
      .toLowerCase();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  }

  // ─────────────────────────── Valores ───────────────────────────

  private readonly VALUE_LABELS: Record<string, string> = {
    true: 'Sí',
    false: 'No',
    yes: 'Sí',
    no: 'No',
    high: 'Alto',
    medium: 'Medio',
    low: 'Bajo'
  };

  /**
   * Formatea un valor técnico: null → '—', booleanos → Sí/No, fechas ISO → fecha local.
   * Con `entity`+`field` traduce además los enums conocidos ("fixed" → "Precio fijo").
   *
   * Las LISTAS se resumen igual que en el backend ("2 elementos: Licencia CRM, Soporte"):
   * volcarlas con JSON.stringify pintaba en la línea de tiempo los ítems crudos de una
   * cotización (`[{"id":19,"unit":"","idQuote":5,…}]`), ilegibles para el usuario.
   */
  formatValue(value: unknown, entity?: string, field?: string): string {
    if (value === undefined || value === null || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Sí' : 'No';
    if (Array.isArray(value)) return this.summarizeList(value);
    const str = typeof value === 'object' ? JSON.stringify(value) : String(value);
    if (entity && field) {
      const enumLabel = this.ENUM_VALUE_LABELS[`${entity}.${field}`]?.[str];
      if (enumLabel) return enumLabel;
    }
    if (this.isIsoDate(str)) return this.datetime.formatDateTime(str);
    return this.VALUE_LABELS[str.toLowerCase()] ?? str;
  }

  private isIsoDate(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?/.test(value);
  }

  /** Claves candidatas a "nombre" de un elemento (mismo orden que el backend). */
  private static readonly ELEMENT_NAME_KEYS = ['itemName', 'name', 'title', 'label', 'description'];

  /** "2 elementos: Licencia CRM, Soporte" — conteo + hasta 3 nombres, como el backend. */
  private summarizeList(list: unknown[]): string {
    const count = list.length === 1 ? '1 elemento' : `${list.length} elementos`;
    const names: string[] = [];
    for (const element of list) {
      if (names.length >= 3) break;
      if (typeof element !== 'object' || element === null) continue;
      const record = element as Record<string, unknown>;
      for (const key of AuditDisplayService.ELEMENT_NAME_KEYS) {
        const value = record[key];
        if (typeof value === 'string' && value.trim() !== '') {
          names.push(value.length > 40 ? value.substring(0, 40) + '…' : value);
          break;
        }
      }
    }
    if (names.length === 0) return count;
    const joined = names.join(', ');
    return `${count}: ${list.length > names.length ? joined + ', …' : joined}`;
  }

  // ─────────────────────────── Diff ───────────────────────────

  /** Campos técnicos que no se muestran en el fallback genérico. */
  private readonly HIDDEN_FIELDS = new Set([
    'id', 'ref', 'businessId', 'idBusiness', 'intIdBussines',
    'datetimeCreated', 'datetimeUpdated', 'createdAt', 'updatedAt'
  ]);

  /**
   * Filas enriquecidas del backend, o `null` si `changesDisplay` no viene
   * (fila antigua / enriquecimiento fallido → el caller decide su fallback).
   * `[]` significa "sin cambios mostrables": no mostrar diff ni caer al fallback.
   */
  displayRows(log: AuditLogResponse): AuditDiffRow[] | null {
    if (log.changesDisplay == null) return null;
    const entity = (log.entityName ?? '').toLowerCase();
    return log.changesDisplay.map(c => ({
      field: c.field,
      label: this.rowLabel(c.field, c.label),
      oldVal: this.formatDisplay(c.oldDisplay ?? c.oldValue, entity, c.field),
      newVal: this.formatDisplay(c.newDisplay ?? c.newValue, entity, c.field)
    }));
  }

  /**
   * Etiqueta de la fila de diff. Se prefiere la del backend, salvo cuando esa etiqueta ES el
   * nombre técnico del campo prettificado ("Price Strategy", "Bundle Price", "Currency Code"):
   * eso significa que la fila se guardó antes de que el backend tuviera el diccionario de esa
   * entidad, y como `changes_display` se persiste, esa etiqueta en inglés se quedaría ahí para
   * siempre. En ese caso se usa la traducción del cliente.
   */
  private rowLabel(field: string, backendLabel?: string | null): string {
    if (!backendLabel) return this.prettifyField(field);
    const own = this.FIELD_LABELS[field];
    return own && this.isRawFieldName(field, backendLabel) ? own : backendLabel;
  }

  /** ¿La etiqueta es el propio nombre del campo con espacios ("Price Strategy" ↔ priceStrategy)? */
  private isRawFieldName(field: string, label: string): boolean {
    const normalize = (v: string) => v.replace(/[\s_-]+/g, '').toLowerCase();
    return normalize(label) === normalize(field);
  }

  private formatDisplay(value?: string | null, entity?: string, field?: string): string {
    if (value === undefined || value === null || value === '') return '—';
    if (entity && field) {
      const enumLabel = this.ENUM_VALUE_LABELS[`${entity}.${field}`]?.[value];
      if (enumLabel) return enumLabel;
    }
    if (this.isIsoDate(value)) return this.datetime.formatDateTime(value);
    return value;
  }

  /**
   * Filas de diff a mostrar: prefiere `changesDisplay`; si es null cae al
   * diff técnico (oldValues/newValues/changedFields) con etiquetas prettificadas
   * y filtrando campos técnicos y sin cambio.
   */
  getDiffRows(log: AuditLogResponse): AuditDiffRow[] {
    return this.displayRows(log) ?? this.technicalRows(log);
  }

  private technicalRows(log: AuditLogResponse): AuditDiffRow[] {
    const oldObj = this.tryParseJson(log.oldValues);
    const newObj = this.tryParseJson(log.newValues);
    if (!oldObj && !newObj) return [];

    const fields = log.changedFields?.length
      ? log.changedFields
      : [...new Set([...Object.keys(oldObj ?? {}), ...Object.keys(newObj ?? {})])];

    // En una actualización con AMBOS snapshots, un campo que solo existe en uno de ellos es una
    // diferencia de FORMA del snapshot (las viejas guardaban el "antes" con la forma de la entidad
    // y el "después" con la del response), no un cambio real: pintarlo como «— → valor» hacía que
    // la actualización se leyera como una cotización creada de cero.
    const isUpdate = log.operationType?.toUpperCase() === 'UPDATE';
    const bothSnapshots = !!oldObj && !!newObj
      && Object.keys(oldObj).length > 0 && Object.keys(newObj).length > 0;

    const entity = (log.entityName ?? '').toLowerCase();
    const isNested = (v: unknown) => typeof v === 'object' && v !== null && !Array.isArray(v);

    return fields
      .filter(f => !this.HIDDEN_FIELDS.has(f))
      .filter(f => !isNested(oldObj?.[f]) && !isNested(newObj?.[f]))
      .filter(f => !isUpdate || !bothSnapshots || (f in (oldObj ?? {}) && f in (newObj ?? {})))
      .map(field => ({
        field,
        label: this.prettifyField(field),
        oldVal: this.formatValue(oldObj?.[field], entity, field),
        newVal: this.formatValue(newObj?.[field], entity, field)
      }))
      .filter(d => d.oldVal !== d.newVal);
  }

  /**
   * ¿Es un UPDATE que el backend ya enriqueció pero sin un solo campo que mostrar?
   *
   * Eran los "registros fantasma": un guardado que no cambió nada quedaba en la línea de
   * tiempo como una actualización sin campos, y el usuario la leía como "actualización con
   * campos vacíos". El backend ya no los genera (la guarda `hasChanges` compara los números
   * por valor), pero los que quedaron en la BD hay que rotularlos en vez de dejarlos mudos.
   */
  isFieldlessUpdate(log: AuditLogResponse): boolean {
    if (log.operationType?.toUpperCase() !== 'UPDATE') return false;
    if (log.changesDisplay == null || log.changesDisplay.length > 0) return false;
    // Solo cuenta como fantasma si SÍ hay snapshots: un evento sin snapshots es un
    // registro de acción cuya descripción ya explica qué pasó ("Moneda base fijada"),
    // y ahí la nota sobraría.
    const keys = (json?: string) => Object.keys(this.tryParseJson(json) ?? {}).length;
    return keys(log.oldValues) > 0 || keys(log.newValues) > 0;
  }

  /** ¿Hay algo que expandir/mostrar como diff para este log? */
  hasDiff(log: AuditLogResponse): boolean {
    if (log.changesDisplay != null) return log.changesDisplay.length > 0;
    return !!(log.oldValues || log.newValues);
  }

  /** Resumen corto: "Ciclo: Prospecto → Calificado · Estado: Activo → Inactivo (+2 más)". */
  getChangeSummary(log: AuditLogResponse): string {
    const rows = this.getDiffRows(log);
    if (rows.length === 0) return '';
    const truncate = (v: string, max = 24) => v.length > max ? v.substring(0, max) + '…' : v;
    const previews = rows.slice(0, 3).map(d => `${d.label}: ${truncate(d.oldVal)} → ${truncate(d.newVal)}`);
    const remaining = rows.length - 3;
    return previews.join('  ·  ') + (remaining > 0 ? `  (+${remaining} más)` : '');
  }

  /** Etiquetas de los campos modificados (chips), preferendo las del backend. */
  getChangedFieldLabels(log: AuditLogResponse): string[] {
    if (log.changesDisplay != null) {
      return log.changesDisplay.map(c => this.rowLabel(c.field, c.label));
    }
    return (log.changedFields ?? [])
      .filter(f => !this.HIDDEN_FIELDS.has(f))
      .map(f => this.prettifyField(f));
  }

  private tryParseJson(json?: string): Record<string, unknown> | null {
    if (!json) return null;
    try { return JSON.parse(json); }
    catch { return null; }
  }

  // ─────────────────────────── Descripción narrativa ───────────────────────────

  /**
   * Las filas nuevas ya traen descripción narrativa en español; las viejas
   * pueden traer texto técnico en inglés — se traduce lo más común.
   *
   * INC 10: además de updated/created/deleted, ahora también detecta verbos
   * de subtypes (disqualify, requalify, scheduled, completed, etc.) que las
   * filas viejas dejaron escritos en inglés en el campo description.
   */
  translateDescription(description?: string | null): string {
    if (!description) return '';
    if (!/(updated|created|deleted|disqualif|requalif|scheduled|completed|sent|received|opened|closed|cancell|reopen|assign|change|added|removed)/i.test(description)) {
      return description;
    }
    return description
      .replace(/\b(company|contact|deal|lead|meeting|quote|task|ticket|product|segment|user)\b/gi,
        m => this.translateEntity(m))
      // Order matters: longer/more specific replacements first.
      .replace(/disqualifies|disqualified|disqualify/gi, 'descalificado')
      .replace(/requalifies|requalified|requalify/gi, 'recalificado')
      .replace(/rescheduled/gi, 'reprogramado')
      .replace(/scheduled/gi, 'programado')
      .replace(/completed/gi, 'completado')
      .replace(/cancelled|canceled/gi, 'cancelado')
      .replace(/reopened|reopen/gi, 'reabierto')
      .replace(/assigned/gi, 'asignado')
      .replace(/changed/gi, 'cambiado')
      .replace(/updated/gi, 'actualizado')
      .replace(/created/gi, 'creado')
      .replace(/deleted/gi, 'eliminado')
      .replace(/added/gi, 'agregado')
      .replace(/removed/gi, 'removido')
      .replace(/\bsent\b/gi, 'enviado')
      .replace(/\breceived\b/gi, 'recibido')
      .replace(/\bopened\b/gi, 'abierto')
      .replace(/\bclosed\b/gi, 'cerrado');
  }

  // ─────────────────────────── Clases de badge / iconos ───────────────────────────

  operationBadgeClass(op?: string | null): string {
    switch (op?.toUpperCase()) {
      case 'CREATE':
      case 'INSERT': return 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400';
      case 'UPDATE': return 'bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400';
      case 'DELETE': return 'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400';
      default: return 'bg-gray-100 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400';
    }
  }

  severityBadgeClass(severity?: string | null): string {
    switch (severity?.toUpperCase()) {
      case 'ERROR': return 'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400';
      case 'WARN':
      case 'WARNING': return 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400';
      default: return 'bg-gray-100 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400';
    }
  }

  /** Path SVG (stroke, viewBox 24) representativo de la entidad para los dots del timeline. */
  entityIconPath(entityName?: string | null): string {
    switch (entityName?.toLowerCase()) {
      case 'contact': return 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z';
      case 'lead': return 'M13 10V3L4 14h7v7l9-11h-7z';
      case 'deal': return 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';
      case 'company':
      case 'bussines':
      case 'business': return 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4';
      case 'meeting': return 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z';
      case 'quote': return 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2';
      case 'task': return 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2';
      case 'task_comment': return 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z';
      case 'task_follower': return 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z';
      case 'task_contact': return 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z';
      case 'task_status_history': return 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z';
      case 'ticket': return 'M15 5v2m0 4v2m0 4v2M5 5h14a2 2 0 012 2v3a2 2 0 000 4v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3a2 2 0 000-4V7a2 2 0 012-2z';
      case 'task_dependency': return 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1';
      case 'product': return 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4';
      default: return 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
    }
  }

  // ─────────────────────────── Niveles de auditoría (settings) ───────────────────────────

  /** Etiqueta corta del nivel: NONE → Desactivado, FULL → Completo. */
  auditLevelLabel(level?: string | null): string {
    switch (level) {
      case 'NONE': return 'Desactivado';
      case 'BASIC': return 'Básico';
      case 'FULL': return 'Completo';
      case 'DETAILED': return 'Detallado';
      default: return level ?? '—';
    }
  }

  /** Etiqueta + descripción corta de lo que registra cada nivel. */
  auditLevelDescription(level?: string | null): string {
    switch (level) {
      case 'NONE': return 'Desactivado — no registra nada';
      case 'BASIC': return 'Básico — registra qué y quién, sin valores';
      case 'FULL': return 'Completo — registra valores antes/después';
      case 'DETAILED': return 'Detallado — además registra IP y navegador';
      default: return level ?? '—';
    }
  }
}
